import { FC, useCallback, useEffect, useRef } from 'react';

import { JabraIcon } from '@gn-audio/jss-components';

import pauseCircle from 'assets/icons/pauseCircle.svg';
import playCircle from 'assets/icons/playCircle.svg';
import { SoundPlayerProps } from 'components/Product/SoundDemo/soundDemoTypes';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

export const SoundDemoPlayer: FC<SoundPlayerProps> = ({
  soundFiles,
  selectedSoundFile,
  isPlaying,
  setIsPlaying,
}) => {
  const { t } = useFallbackTranslation();
  const audioRefs = useRef<Array<HTMLAudioElement | null>>(soundFiles?.map(() => null) || []);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      audioRefs.current.forEach((audio) => {
        if (audio) audio.pause();
      });
    } else {
      audioRefs.current.forEach((audio) => {
        if (audio && soundFiles) {
          audio.currentTime = 0;
          audio.play();
        }
      });
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, soundFiles, setIsPlaying]);

  useEffect(() => {
    if (isPlaying && soundFiles?.length) {
      soundFiles.forEach((file, index) => {
        const audio = audioRefs.current[index];
        if (audio && file?.SoundDemoName?.value) {
          audio.muted = file.SoundDemoName.value !== selectedSoundFile;
        }
      });
    }
  }, [isPlaying, soundFiles, selectedSoundFile]);

  return (
    <>
      <button
        className="flex h-16 w-full items-center justify-center gap-2 p-2 focus-visible:rounded-full focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:outline-focus"
        onClick={handlePlayPause}
      >
        <JabraIcon Icon={isPlaying ? pauseCircle : playCircle} />
        <span className="truncate text-left capitalize">
          {t(isPlaying ? 'system.PauseDemo' : 'system.PlayDemo')}
        </span>
      </button>
      {soundFiles?.map(
        (file, index) =>
          !!file.SoundDemoFile.value && (
            <audio
              key={index}
              ref={(el: HTMLAudioElement | null) => {
                audioRefs.current[index] = el;
              }}
              src={file.SoundDemoFile?.value.src}
              onEnded={() => {
                if (audioRefs.current.every((audio) => audio?.paused || audio?.ended)) {
                  setIsPlaying(false);
                }
              }}
            />
          )
      )}
    </>
  );
};
